import i18n from "../i18n";
import useReaderStore from "../context/readerStore";
import { isFeatureAllowed } from "./drmPolicyService";
import {
  createUnderlineApi,
  deleteUnderlineApi,
  fetchAccessData,
  getSessionTokens,
  getValidRstToken,
} from "./igpService";
import {
  showFeatureNotAllowedNotification,
  showOperationFailedNotification,
  showSessionExpiredNotification,
} from "./notificationService";

/**
 * PDF Underline service for managing PDF text underlines.
 * Uses IGP API as source-of-truth.
 *
 * PDF underlines use pixel-based coordinates:
 * - left: X position relative to page canvas (in PDF units, unscaled)
 * - top: Y position at baseline of text (in PDF units, unscaled)
 * - width: Width of underlined text
 * - height: Height of underline (typically 1-2 pixels)
 */

export async function refreshUnderlinesFromApi() {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    const accessData = await fetchAccessData(grantId, rstToken, ["underline"]);
    const underlines = accessData?.underline?.data || [];
    const { setAnnotationData } = useReaderStore.getState();
    setAnnotationData("underlines", underlines);
    return underlines;
  } catch (err) {
    console.error("Error refreshing underlines from API:", err);
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    }
    return [];
  }
}

/**
 * Internal: Persist underline to API in background with error handling.
 * Removes from store on error.
 */
async function _persistUnderlineToApi(operation, underlineId, payload) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);

    if (operation === "create") {
      await createUnderlineApi(grantId, rstToken, payload);
    }
  } catch (err) {
    console.error(`Error persisting PDF underline (${operation}):`, err);

    // Rollback: Remove from store on error
    const state = useReaderStore.getState();
    const current = state.annotations.underlines.data || [];
    state.setAnnotationData(
      "underlines",
      current.filter((u) => u.id !== underlineId),
    );

    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.saveUnderline"));
    }
  }
}

/**
 * Creates a new PDF underline annotation using OPTIMISTIC UPDATES.
 * Updates the store immediately and persists to API in background.
 *
 * @param {Object} underlineData - { page_number, content, coordinates }
 * @returns {Object} The optimistic underline object
 */
export function createPdfUnderline(underlineData) {
  // Enforce DRM policy
  const drmPolicy = useReaderStore.getState().drmPolicy;
  if (!isFeatureAllowed(drmPolicy, "underline")) {
    showFeatureNotAllowedNotification("underline");
    return null;
  }

  // Validate required fields
  if (!underlineData.page_number || !underlineData.coordinates) {
    throw new Error(
      "PDF underline requires page_number and coordinates (left, top, width, height)",
    );
  }

  const requestId =
    underlineData.id || `pul_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const payload = {
    id: requestId,
    page_number: underlineData.page_number,
    content: underlineData.content || "",
    timestamp: new Date().toISOString(),
    _isPending: true,
  };

  // Support single-rect coordinates or an array of rects (like highlights)
  if (Array.isArray(underlineData.coordinates)) {
    payload.coordinates = underlineData.coordinates.map((c) => ({
      left: c.left || 0,
      top: c.top || 0,
      width: c.width || 0,
      height: c.height || 1,
    }));
  } else {
    payload.coordinates = {
      left: underlineData.coordinates.left || 0,
      top: underlineData.coordinates.top || 0,
      width: underlineData.coordinates.width || 0,
      height: underlineData.coordinates.height || 1,
    };
  }

  // Update store immediately (optimistic update)
  const state = useReaderStore.getState();
  const current = state.annotations.underlines.data || [];
  state.setAnnotationData("underlines", [...current, payload]);

  // Fire-and-forget API call in background
  _persistUnderlineToApi("create", requestId, payload);

  return payload;
}

/**
 * Resolve to the API (parent) underline id.
 */
function toApiUnderlineId(underlineId) {
  if (!underlineId || typeof underlineId !== "string") return underlineId;
  return underlineId.replace(/-rect-\d+$/, "") || underlineId;
}

/**
 * Deletes a PDF underline by ID using OPTIMISTIC UPDATES.
 * Removes from store immediately.
 *
 * @param {string} underlineId - The ID of the underline to delete
 * @returns {Object|null} The deleted underline (for possible rollback)
 */
export function deletePdfUnderline(underlineId) {
  const state = useReaderStore.getState();
  const current = state.annotations.underlines.data || [];
  const apiId = toApiUnderlineId(underlineId);
  const underlineToDelete = current.find((u) => toApiUnderlineId(u.id) === apiId);

  if (!underlineToDelete) return null;

  // Remove from store immediately (optimistic update)
  state.setAnnotationData(
    "underlines",
    current.filter((u) => toApiUnderlineId(u.id) !== apiId),
  );

  // Fire-and-forget API call in background
  _persistUnderlineDelete(apiId, underlineToDelete);

  return underlineToDelete;
}

/**
 * Internal: Special handling for delete with rollback.
 */
async function _persistUnderlineDelete(underlineId, underlineData) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    await deleteUnderlineApi(grantId, rstToken, underlineId);
  } catch (err) {
    console.error("Error deleting PDF underline:", err);

    // Rollback: Re-add to store
    const state = useReaderStore.getState();
    const current = state.annotations.underlines.data || [];
    state.setAnnotationData("underlines", [...current, underlineData]);

    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.deleteUnderline"));
    }
  }
}


/**
 * Checks if a new underline would be a duplicate of an existing one.
 * Prevents multiple underlines on the same text position.
 * @param {Array} existingUnderlines - Array of existing underlines on the page
 * @param {Object} newUnderline - New underline to check
 * @param {number} tolerance - Pixel tolerance for considering overlaps (default: 2)
 * @returns {boolean} True if duplicate/overlapping, false otherwise
 */
export function isDuplicateUnderline(
  existingUnderlines,
  newUnderline,
  tolerance = 2,
) {
  return existingUnderlines.some((existing) => {
    if (!existing?.coordinates) return false;
    // Check if coordinates are almost identical (within tolerance)
    const leftMatch =
      Math.abs((existing.coordinates.left ?? 0) - newUnderline.left) <=
      tolerance;
    const topMatch =
      Math.abs((existing.coordinates.top ?? 0) - newUnderline.top) <= tolerance;
    const widthMatch =
      Math.abs((existing.coordinates.width ?? 0) - newUnderline.width) <=
      tolerance;

    return leftMatch && topMatch && widthMatch;
  });
}

export default useReaderStore;
